import ApiGateway, {
    ApiResponse,
    CustomAxiosResponse,
    PureGateway,
    PureGatewayWithoutAuth,
} from '@app/helpers/AxiosHelper';
import { getFormData } from '@app/helpers/ObjectHelper';

import type {
    AddressingMesh,
    ApiCustomerActivities,
    ApiCustomerVehicle,
    ApiLeadListingParam,
    ApvCounterByStatusResponse,
    ApvCounterResponse,
    ApvCounters,
    ApvCounterStatus,
    CreateSalesforceLead,
    Customer,
    CustomerFeedback,
    ExternalData,
    LeadDetails,
    Review,
} from '@app/crud/customers/customer.type';
import { NotesResult } from '@app/crud/note/note.type';

import { ActionsBlock } from '@app/pages/customers/Partials/ActionsBlock/ActionsBlock.type';
import { Consent, Lead } from '@app/store/customers/customers.store';
import store from '@app/store/store';

const { REACT_APP_LEAD_URL } = process.env;

const getUserData = () => store.getState().auth.userData;

export interface GetCustomersParams {
    research?: string;
    vehicle?: string;
    companyName?: string;
    siret?: string;
    siren?: string;
    isDmsPro?: boolean;
    contactName?: string;
    contactId?: number;
    contactInfoContact?: string;
    contactCity?: string;
    dealershipKnown?: string;
    vehicleKnown?: string;
    ownerName?: string;
    page?: number;
    items?: number;
    sort?: string;
    leadStatusName?: string;
    contactUpdatedPeriod?: string;
    email?: string;
    contactIsPro?: boolean;
}

export function getCustomers(params: GetCustomersParams) {
    return ApiGateway.get<ApiResponse<Customer[]>, CustomAxiosResponse<ApiResponse<Customer[]>>>(
        `${REACT_APP_LEAD_URL}/contact`,
        {
            params,
        },
    );
}

export function getCustomersPortfolio(params: GetCustomersParams) {
    return ApiGateway.get<ApiResponse<Customer[]>, CustomAxiosResponse<ApiResponse<Customer[]>>>(
        `${REACT_APP_LEAD_URL}/contact/client-portfolio`,
        {
            params,
        },
    );
}

export function getCustomerWithActiveAction(params: GetCustomersParams) {
    return ApiGateway.get<ApiResponse<Customer[]>, CustomAxiosResponse<ApiResponse<Customer[]>>>(
        `${REACT_APP_LEAD_URL}/contact/active-action`,
        {
            params,
        },
    );
}

export async function getCustomerCounters() {
    const { count } = await ApiGateway.get<ApiResponse<[]>, CustomAxiosResponse<ApiResponse<[]>>>(
        `${REACT_APP_LEAD_URL}/contact/client-portfolio-count`,
    );
    return count;
}

interface ApiCustomer {
    error?: boolean;
    contact: Customer;
    feedbacks?: CustomerFeedback[];
    externalData: ExternalData[] | null;
    leads: Lead[];
    differentialsVCU: [];
    noteCounter: number;
}

export function getCustomer(id: string) {
    return ApiGateway.get<ApiResponse<ApiCustomer>, CustomAxiosResponse<ApiResponse<ApiCustomer>>>(
        `${REACT_APP_LEAD_URL}/contact/${id}`,
    );
}

export interface ApiActionData {
    type: 'business' | 'activity';
    error?: boolean;
}

export function getActionData(id: string) {
    return ApiGateway.get<ApiResponse<ApiActionData>, CustomAxiosResponse<ApiResponse<ApiActionData>>>(
        `${REACT_APP_LEAD_URL}/contact/business?vcuId=${id}`,
    );
}

export function getEventsCustomerLife(id: string) {
    return ApiGateway.get<ApiResponse<ApiCustomerVehicle[]>, CustomAxiosResponse<ApiResponse<ApiCustomerVehicle[]>>>(
        `${REACT_APP_LEAD_URL}/contact/${id}/events/client-life`,
    );
}

export function getOneCustomerActivity(id: string) {
    return ApiGateway.get<ApiResponse<LeadDetails>, CustomAxiosResponse<ApiResponse<LeadDetails>>>(
        `${REACT_APP_LEAD_URL}/lead/${id}`,
    );
}

export function getOneCustomerActivities(id: string) {
    return ApiGateway.get<ApiResponse<ApiCustomerActivities>, CustomAxiosResponse<ApiResponse<ApiCustomerActivities>>>(
        `${REACT_APP_LEAD_URL}/lead/${id}/activities`,
    );
}

export function getCustomerNotes(id: number) {
    return ApiGateway.get<NotesResult, CustomAxiosResponse<NotesResult>>(`${REACT_APP_LEAD_URL}/contact/${id}/notes`);
}

export function getCustomerActions(id: number) {
    return ApiGateway.get<ApiResponse<ActionsBlock>, CustomAxiosResponse<ApiResponse<ActionsBlock>>>(
        `${REACT_APP_LEAD_URL}/contact/${id}/bloc_action`,
    );
}

export interface Action {
    adminForce?: boolean;
    reason?: string;
    result?: string;
    actionType: string;
    contactId: number;
    emailType?: string;
    object?: string;
    content?: string;
    recipient?: string;
    lead?: {
        id: number;
        type?: string;
        source?: string;
        origin?: string;
        dealershipId?: string;
        dealershipName?: string;
    };
    user: {
        id: number;
        firstname: string;
        lastname: string;
        email: string;
    };
    dealership?: {
        id: string;
        name: string;
    };
    newOwner?: {
        id: string;
        firstname: string;
        lastname: string;
        email: string;
    };
}

interface ActionReturn {
    leads: Lead[];
    lastLeadId: number;
    lead?: Lead;
}

export function addAction(data: Action, form = false) {
    return ApiGateway.post<ApiResponse<ActionReturn>, CustomAxiosResponse<ApiResponse<ActionReturn>>>(
        `${REACT_APP_LEAD_URL}/action`,
        form ? getFormData(data) : data,
    );
}

export function addActionConsignReportController(data: { lead: number; comment: string }) {
    return ApiGateway.post<ApiResponse<void>, CustomAxiosResponse<ApiResponse<void>>>(
        `${REACT_APP_LEAD_URL}/action/consign-report-controlled`,
        data,
    );
}

export function addCustomer(data: Customer) {
    const user = getUserData();

    return ApiGateway.post<ApiResponse<Customer>, CustomAxiosResponse<ApiResponse<Customer>>>(
        `${REACT_APP_LEAD_URL}/contact`,
        {
            ...data,
            user: {
                id: user.id,
                firstname: user.first_name,
                lastname: user.last_name,
            },
        },
    );
}

export function updateCustomerById(id: string, data: Customer) {
    return ApiGateway.put<ApiResponse<Customer>, CustomAxiosResponse<ApiResponse<Customer>>>(
        `${REACT_APP_LEAD_URL}/contact/${id}`,
        data,
    );
}

export function getAllParams(params?: object) {
    return ApiGateway.get<ApiLeadListingParam, CustomAxiosResponse<ApiLeadListingParam>>(
        `${REACT_APP_LEAD_URL}/listing-param`,
        {
            params,
        },
    );
}

export function getAlreadyExistedCustomers(params: object): Promise<CustomAxiosResponse<ApiResponse<Customer[]>>> {
    return ApiGateway.get<ApiResponse<Customer[]>, CustomAxiosResponse<ApiResponse<Customer[]>>>(
        `${REACT_APP_LEAD_URL}/contact/check-data`,
        {
            params,
        },
    );
}

export function getCustomerEvents(params: { id: string; start?: number; items?: number; isClientLife?: boolean }) {
    const id = params?.id;
    delete params?.id;

    return ApiGateway.get(`${REACT_APP_LEAD_URL}/contact/${id}/events`, {
        params,
    });
}

export function getCustomerEventById(id: string) {
    return ApiGateway.get(`${REACT_APP_LEAD_URL}/contact/events/${id}`);
}

export function associateContactToLead(leadId: string, contactId: string) {
    return ApiGateway.post(`${REACT_APP_LEAD_URL}/lead/${leadId}/associate-contact/${contactId}`);
}

export async function getContactInquiryNewCounter() {
    // @ts-ignore
    const { result } = await ApiGateway.get(`${REACT_APP_LEAD_URL}/inquiries/counter/new`);
    return result;
}

export async function getContactInquiryInProgressCounter() {
    // @ts-ignore
    const { result } = await ApiGateway.get(`${REACT_APP_LEAD_URL}/inquiries/counter/in_progress`);
    return result;
}

export async function getContactInquiryAllCounter() {
    // @ts-ignore
    const { result } = await ApiGateway.get(`${REACT_APP_LEAD_URL}/inquiries/counter/all`);
    return result;
}

export async function getContactInquiryClosedCounter() {
    // @ts-ignore
    const { result } = await ApiGateway.get(`${REACT_APP_LEAD_URL}/inquiries/counter/closed`);
    return result;
}

export async function getAchatCashNewCounter() {
    // @ts-ignore
    const { result } = await ApiGateway.get(`${REACT_APP_LEAD_URL}/counter/achat-cash/new`);
    return result;
}

export async function getAchatCashActionToDoCounter() {
    // @ts-ignore
    const { result } = await ApiGateway.get(`${REACT_APP_LEAD_URL}/counter/achat-cash/action_to_do`);
    return result;
}

export async function getAchatCashActionToComeCounter() {
    // @ts-ignore
    const { result } = await ApiGateway.get(`${REACT_APP_LEAD_URL}/counter/achat-cash/action_to_come`);
    return result;
}

export async function getAchatCashAllCounter() {
    // @ts-ignore
    const { result } = await ApiGateway.get(`${REACT_APP_LEAD_URL}/counter/achat-cash/all`);
    return result;
}

export async function getAchatCashCloseCounter() {
    // @ts-ignore
    const { result } = await ApiGateway.get(`${REACT_APP_LEAD_URL}/counter/achat-cash/close`);
    return result;
}

export async function getAchatCashStockCounter() {
    // @ts-ignore
    const { result } = await ApiGateway.get(`${REACT_APP_LEAD_URL}/counter/achat-cash/stock`);
    return result;
}

export async function getFluidityInProgressCounter() {
    // @ts-ignore
    const { result } = await ApiGateway.get(`${REACT_APP_LEAD_URL}/counter/lead-fluidity/in_progress`);
    return result;
}

export async function getFluidityCloseCounter() {
    // @ts-ignore
    const { result } = await ApiGateway.get(`${REACT_APP_LEAD_URL}/counter/lead-fluidity/close`);
    return result;
}

// APV Counter functions
export async function getApvCounters(): Promise<ApvCounters> {
    try {
        const response = await ApiGateway.get<ApvCounterResponse, CustomAxiosResponse<ApvCounterResponse>>(
            `${REACT_APP_LEAD_URL}/counter/apv`,
        );

        if (response.result?.error) {
            throw new Error(response.result.error);
        }

        return (
            response.result || {
                apv_new: 0,
                apv_action_to_do: 0,
                apv_action_to_come: 0,
                apv_all: 0,
                apv_close: 0,
            }
        );
    } catch {
        throw new Error('Failed to fetch APV counters');
    }
}

export async function getApvCounterByStatus(status: ApvCounterStatus): Promise<number> {
    try {
        const response = await ApiGateway.get<
            ApvCounterByStatusResponse,
            CustomAxiosResponse<ApvCounterByStatusResponse>
        >(`${REACT_APP_LEAD_URL}/counter/apv/${status}`);

        if (response.result?.error) {
            throw new Error(response.result.error);
        }

        return response.result || 0;
    } catch {
        throw new Error(`Failed to fetch APV counter for status: ${status}`);
    }
}

export function rollBackLead(id: number) {
    return ApiGateway.delete(`${REACT_APP_LEAD_URL}/lead/${id}/rollback`);
}

interface RpaActionParams {
    action: string;
    app: string;
    contactId: number;
    rpaId?: string;
    workflowId?: number;
}

export function rpaAction(data: RpaActionParams) {
    return ApiGateway.post(`${REACT_APP_LEAD_URL}/rpa/transfer`, {
        ...data,
    });
}

export function getRpaWorkflow(idWorkflow: string) {
    return ApiGateway.get(`${REACT_APP_LEAD_URL}/rpa/workflow/${idWorkflow}`);
}

type ConsentByToken = {
    consents: {
        email: boolean | null;
        pixel: boolean | null;
        sms: boolean | null;
        phone: boolean | null;
        post: boolean | null;
    };
    dealership: {
        name: string;
        address: string;
        zipcode: string;
        city: string;
        context: string;
    };
};

export function getConsentByToken(token: string) {
    return PureGatewayWithoutAuth.get<ApiResponse<ConsentByToken>, CustomAxiosResponse<ApiResponse<ConsentByToken>>>(
        `${REACT_APP_LEAD_URL}/consent/${token}`,
    );
}

type UpdatedConsent = {
    id: number;
    name: string;
    slug: string;
    refDealership: string;
    gdprSms: boolean | null;
    gdprPhone: boolean | null;
    gdprPost: boolean | null;
    gdprEmail: boolean | null;
    gdprPixel: boolean | null;
    clientToken: string;
    created: string;
    updated: string;
};

export function updateConsentByToken(token: string, data: Consent) {
    return PureGatewayWithoutAuth.put<ApiResponse<UpdatedConsent>, CustomAxiosResponse<ApiResponse<UpdatedConsent>>>(
        `${REACT_APP_LEAD_URL}/consent/${token}`,
        data,
    );
}

export function createCustomerNote(data: { message: string; type: string; contactId: number }) {
    return ApiGateway.post(`${REACT_APP_LEAD_URL}/contact-note`, data);
}

export interface InStockVehicle {
    sku: string;
}

export interface OutStockVehicle {
    make: string;
    model: string;
    fuelType?: string;
    gearsType?: string;
}

export type CommercialLeadPayload = {
    contact: number;
    dealership: string;
    leadType: string;
    customerProject: string;
    autouserSource?: number;
    vehicles?: (InStockVehicle | OutStockVehicle)[] | null;
    appointment: {
        subject: string;
        dateStart: string;
        dateEnd: string;
        comment: string;
        notifyCustomer: boolean;
    };
};

export function createCommercialLead(data: CommercialLeadPayload) {
    return ApiGateway.post(`${REACT_APP_LEAD_URL}/action/create-lead/commercial`, data);
}

export type ReceptionistNotePayload = {
    contactId: number;
    type: 'sales';
    message: string;
    noteTypeSlug: string;
    dealershipSource: string;
    referentId?: number;
    team: string;
};

export function createSalesforceLead(id: number) {
    return ApiGateway.post<ApiResponse<CreateSalesforceLead>, CustomAxiosResponse<ApiResponse<CreateSalesforceLead>>>(
        `${REACT_APP_LEAD_URL}/lead/${id}/create-salesforce-lead`,
    );
}

export function createContactFromVcu(vcuId: string) {
    return ApiGateway.post<ApiResponse<Customer>, CustomAxiosResponse<ApiResponse<Customer>>>(
        `${REACT_APP_LEAD_URL}/contact/create/from-vcu-id/${vcuId}`,
    );
}

export function createContactFromLead(leadId: string) {
    return ApiGateway.post<ApiResponse<Customer>, CustomAxiosResponse<ApiResponse<Customer>>>(
        `${REACT_APP_LEAD_URL}/contact/create/from-lead-origin/${leadId}`,
    );
}

export function bernardConsentUpdateRequest(contactId: number) {
    return ApiGateway.post<ApiResponse<{ success: boolean }>, CustomAxiosResponse<ApiResponse<{ success: boolean }>>>(
        `${REACT_APP_LEAD_URL}/consent/bernard-update/${contactId}`,
    );
}

export function fetchCustomersBySiret(siret: string) {
    return ApiGateway.get<ApiResponse<Customer[]>, CustomAxiosResponse<ApiResponse<Customer[]>>>(
        `${REACT_APP_LEAD_URL}/contact/siret/${siret}`,
    );
}

export function fetchAddressingMeshesBySiret(siret: string) {
    return ApiGateway.get<ApiResponse<AddressingMesh[]>, CustomAxiosResponse<ApiResponse<AddressingMesh[]>>>(
        `${REACT_APP_LEAD_URL}/contact/addressing-meshes/${siret}`,
    );
}

export type PostCreateDmsParams = {
    baseDmsIdentifier: string;
    id: string;
};

export function createContactDms(args: PostCreateDmsParams) {
    const { baseDmsIdentifier, id } = args;

    return ApiGateway.post<
        ApiResponse<Customer['contactDms']>,
        CustomAxiosResponse<ApiResponse<Customer['contactDms']>>
    >(`${REACT_APP_LEAD_URL}/contact/create-contact-dms/${id}`, {
        baseDmsIdentifier,
    }).catch((err) => err);
}

export type PostCreateDmsFromVcuResponse = { contact: Customer; dmsError: string | null };

export function createContactDmsFromVcu(args: PostCreateDmsParams) {
    const { baseDmsIdentifier, id } = args;

    return ApiGateway.post<
        ApiResponse<PostCreateDmsFromVcuResponse>,
        CustomAxiosResponse<ApiResponse<PostCreateDmsFromVcuResponse>>
    >(`${REACT_APP_LEAD_URL}/contact/create/from-vcu-id-to-dms/${id}`, {
        baseDmsIdentifier,
    });
}

export type GenerateContactSynthesisResponse = { synthesis: string | null; error?: string; errorCode?: string };

type ContactSynthesisApiResponse = ApiResponse<GenerateContactSynthesisResponse>;
type ContactSynthesisAxiosResponse = CustomAxiosResponse<ContactSynthesisApiResponse>;

export function generateContactSynthesis(contactId: number) {
    return ApiGateway.get<ContactSynthesisApiResponse, ContactSynthesisAxiosResponse>(
        `${REACT_APP_LEAD_URL}/contact/synthesis/generate/${contactId}`,
    );
}

export function reGenerateContactSynthesis(contactId: number) {
    return ApiGateway.get<ContactSynthesisApiResponse, ContactSynthesisAxiosResponse>(
        `${REACT_APP_LEAD_URL}/contact/synthesis/regenerate/${contactId}`,
    );
}
